import React, { useState } from 'react';
import { List, Show, useListContext, useShowContext, useRefresh, useDataProvider, useGetList } from 'react-admin';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Award, CheckCircle, XCircle, ExternalLink, Copy, Check, X as XIcon, Clock } from 'lucide-react';

import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog, ConfirmTone } from '../components/ui/confirm-dialog';
import { Avatar, AvatarName } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';

/* ============================================================
   Specialty Certificates — Latio design system.
   Admin review queue for FEAT-028: a trainer holding a speciality that
   requires certification submits documents here for approval. Modeled on
   the trainer-applications review queue (list + full-page detail), trimmed
   to what this resource actually supports: only PENDING rows are
   actionable (approve/reject); GRACE_PERIOD/APPROVED/REJECTED/EXPIRED are
   read-only states. No status-history endpoint exists for this resource
   (unlike trainer applications), so there's no History button here.
   ============================================================ */

export interface SpecialtyCertificate {
  id: number;
  trainer: {
    id: number;
    name: string;
    email: string;
    username?: string;
    profile_picture_url?: string;
  };
  sport: {
    id: number;
    key: string;
    name_en: string;
    name_es?: string;
    name_pt?: string;
    category?: string;
    icon_url?: string;
  };
  status: 'GRACE_PERIOD' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  document_urls: string | null;
  grace_period_deadline: string | null;
  rejection_reason: string | null;
  created_at: string;
}

/* ---- Status meta (label + tone) ---- */
const CERT_STATUS: Record<
  string,
  { label: string; tone: 'indigo' | 'warning' | 'success' | 'error' | 'neutral' }
> = {
  GRACE_PERIOD: { label: 'Grace period', tone: 'warning' },
  PENDING: { label: 'Pending', tone: 'indigo' },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'error' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
};

const TONE_CLS: Record<string, string> = {
  indigo: 'bg-indigo-tint text-indigo',
  warning: 'bg-warning-bg text-warning',
  success: 'bg-success-bg text-success',
  error: 'bg-error-bg text-error',
  neutral: 'bg-surface-2 text-ink-2',
};
const TONE_DOT: Record<string, string> = {
  indigo: 'bg-indigo',
  warning: 'bg-warning',
  success: 'bg-success',
  error: 'bg-error',
  neutral: 'bg-ink-3',
};

export function CertStatusBadge({ status }: { status?: string }) {
  const meta = CERT_STATUS[status || ''] ?? { label: status || '—', tone: 'neutral' as const };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-semibold',
        TONE_CLS[meta.tone],
      )}
    >
      <span className={cn('size-1.5 rounded-full', TONE_DOT[meta.tone])} aria-hidden />
      {meta.label}
    </span>
  );
}

export function fmtDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

function safeParse(json?: string | null): string[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

/* ---- Pill button ---- */
function PillBtn({
  children,
  onClick,
  variant = 'outline',
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'outline' | 'danger' | 'success';
  disabled?: boolean;
}) {
  const styles = {
    primary: 'bg-black text-white hover:bg-ink',
    success: 'bg-success text-white hover:bg-success/90',
    danger: 'bg-error text-white hover:bg-error/90',
    outline: 'border border-hairline text-ink hover:bg-surface-2',
  }[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-pill px-4 text-sm font-semibold transition-colors duration-150 disabled:opacity-50',
        styles,
      )}
    >
      {children}
    </button>
  );
}

function DetailCard({
  title,
  icon: Icon,
  children,
  className,
}: {
  title?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

function SportLabel({ sport }: { sport: SpecialtyCertificate['sport'] }) {
  return (
    <div className="flex items-center gap-2">
      {sport.icon_url ? (
        <img src={sport.icon_url} alt="" className="size-6 rounded-latio-sm object-cover" />
      ) : (
        <span className="flex size-6 items-center justify-center rounded-latio-sm bg-surface-2 text-ink-3">
          <Award className="size-3.5" />
        </span>
      )}
      <span className="font-medium text-ink">{sport.name_en || sport.key}</span>
    </div>
  );
}

/* ---- Document gallery (reused shape from trainer-applications) ---- */
const MediaViewerModal = ({
  src,
  open,
  onClose,
}: {
  src: string;
  open: boolean;
  onClose: () => void;
}) => {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied by the browser; not actionable here.
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <span>Certificate document</span>
          <button onClick={onClose} className="text-ink-2 hover:text-ink">
            ✕
          </button>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="space-y-4">
          <div className="flex justify-center">
            <img
              src={src}
              alt="Certificate document"
              className="max-h-96 max-w-full rounded-card object-contain"
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          </div>
          <div className="flex items-center gap-2 rounded-input border border-hairline bg-surface-2 p-2">
            <code className="flex-1 break-all text-sm text-ink-2">{src}</code>
            <button
              onClick={() => copyToClipboard(src)}
              className="flex items-center justify-center rounded border border-hairline p-1 hover:bg-background"
              title="Copy URL"
            >
              <Copy className="size-3" />
            </button>
            <button
              onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
              className="flex items-center justify-center rounded border border-hairline p-1 hover:bg-background"
              title="Open in new tab"
            >
              <ExternalLink className="size-3" />
            </button>
          </div>
          {copied && <p className="text-xs text-success">URL copied to clipboard!</p>}
        </div>
      </DialogContent>
      <DialogActions>
        <PillBtn onClick={onClose}>Close</PillBtn>
      </DialogActions>
    </Dialog>
  );
};

const DocumentGallery = ({ urls }: { urls: string[] }) => {
  const [selected, setSelected] = useState<string | null>(null);
  if (!urls || urls.length === 0) {
    return <p className="text-sm text-ink-2">No documents submitted yet</p>;
  }
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {urls.map((url, i) => (
          <div
            key={i}
            className="relative aspect-square cursor-pointer overflow-hidden rounded-latio-sm border border-hairline"
            onClick={() => setSelected(url)}
          >
            <img
              src={url}
              alt={`Certificate document ${i + 1}`}
              className="size-full object-cover"
              onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
            />
          </div>
        ))}
      </div>
      {selected && (
        <MediaViewerModal src={selected} open={!!selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
};

/* ============================================================
   LIST
   ============================================================ */
const STATUS_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'GRACE_PERIOD', label: 'Grace period' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'EXPIRED', label: 'Expired' },
];

const SORT_FIELDS = [
  { field: 'created_at', label: 'Submitted' },
  { field: 'status', label: 'Status' },
  { field: 'id', label: 'ID' },
];
const SORT_LABELS = Object.fromEntries(SORT_FIELDS.map((f) => [f.field, f.label]));

function StatusTabs() {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.status_filter as string) || 'ALL';
  return (
    <div className="max-w-full overflow-x-auto">
      <SegmentedTabs
        tabs={STATUS_TABS}
        value={current}
        onChange={(v) =>
          setFilters({ ...filterValues, status_filter: v === 'ALL' ? undefined : v }, displayedFilters)
        }
      />
    </div>
  );
}

function CertificateStats() {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const pending = useGetList('specialty-certificates', { ...base, filter: { status_filter: 'PENDING' } });
  const gracePeriod = useGetList('specialty-certificates', { ...base, filter: { status_filter: 'GRACE_PERIOD' } });
  const approved = useGetList('specialty-certificates', { ...base, filter: { status_filter: 'APPROVED' } });
  const all = useGetList('specialty-certificates', { ...base, filter: {} });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Pending review" value={n(pending.total)} />
      <StatCard label="Grace period" value={n(gracePeriod.total)} />
      <StatCard label="Approved" value={n(approved.total)} />
      <StatCard label="Total" value={n(all.total)} />
    </StatRow>
  );
}

function CertificatesListBody() {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const refresh = useRefresh();
  const [rejectTarget, setRejectTarget] = useState<SpecialtyCertificate | null>(null);

  const approve = async (record: SpecialtyCertificate) => {
    try {
      await dataProvider.update('specialty-certificates', {
        id: record.id,
        data: { approved: true },
        previousData: record,
      });
      toastSuccess('Certificate approved', record.trainer.name);
      refresh();
    } catch {
      toastError('Failed to approve certificate');
    }
  };

  const reject = async (reason?: string) => {
    if (!rejectTarget) return;
    try {
      await dataProvider.update('specialty-certificates', {
        id: rejectTarget.id,
        data: { approved: false, rejection_reason: reason },
        previousData: rejectTarget,
      });
      toastSuccess('Certificate rejected', rejectTarget.trainer.name);
      refresh();
    } catch {
      toastError('Failed to reject certificate');
    }
  };

  const columns: DataColumn<SpecialtyCertificate>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      width: '1px',
      cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span>,
    },
    {
      key: 'trainer',
      header: 'Trainer',
      cell: (r) => (
        <AvatarName src={r.trainer.profile_picture_url} name={r.trainer.name} subtext={r.trainer.email} />
      ),
    },
    { key: 'sport', header: 'Specialty', cell: (r) => <SportLabel sport={r.sport} /> },
    { key: 'status', header: 'Status', sortable: true, cell: (r) => <CertStatusBadge status={r.status} /> },
    { key: 'created_at', header: 'Submitted', sortable: true, cell: (r) => fmtDate(r.created_at) },
    {
      key: 'grace_period_deadline',
      header: 'Grace deadline',
      cell: (r) =>
        r.status === 'GRACE_PERIOD' ? (
          <span className="inline-flex items-center gap-1 text-warning">
            <Clock className="size-3.5" />
            {fmtDate(r.grace_period_deadline)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) =>
        r.status === 'PENDING' ? (
          <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => approve(r)}
              title="Approve"
              className="flex size-8 items-center justify-center rounded-pill border border-hairline text-success hover:bg-success-bg"
            >
              <Check className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => setRejectTarget(r)}
              title="Reject"
              className="flex size-8 items-center justify-center rounded-pill border border-hairline text-error hover:bg-error-bg"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <ListScreen
        stats={<CertificateStats />}
        tabs={<StatusTabs />}
        actions={<SortMenu fields={SORT_FIELDS} />}
        tokens={<FilterTokens fields={[]} sortLabels={SORT_LABELS} />}
      >
        <DataTable<SpecialtyCertificate> columns={columns} rowClick="show" emptyMessage="No specialty certificates found." />
        <div className="pt-1">
          <ReactAdminPagination />
        </div>
      </ListScreen>

      <ConfirmDialog
        open={!!rejectTarget}
        onOpenChange={(o) => !o && setRejectTarget(null)}
        tone="destructive"
        title="Reject certificate"
        consequence="The trainer will see your reason and can resubmit."
        confirmLabel="Reject certificate"
        reasonRequired
        reasonChips={['Illegible document', 'Does not match specialty', 'Certificate expired or invalid']}
        onConfirm={reject}
      />
    </>
  );
}

export const SpecialtyCertificateList = (props: any) => (
  <List
    {...props}
    actions={false}
    empty={false}
    pagination={false}
    sort={{ field: 'created_at', order: 'DESC' }}
    perPage={25}
    component="div"
  >
    <CertificatesListBody />
  </List>
);

/* ============================================================
   DETAIL
   ============================================================ */
type ConfirmConfig = {
  title: string;
  consequence?: string;
  tone: ConfirmTone;
  confirmLabel: string;
  reasonRequired?: boolean;
  reasonChips?: string[];
  run: (reason?: string) => Promise<void>;
};

const SpecialtyCertificateShowContent = () => {
  const { record, isLoading } = useShowContext<SpecialtyCertificate>();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const refresh = useRefresh();
  const [confirm, setConfirm] = useState<ConfirmConfig | null>(null);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading certificate…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Certificate not found</div>;

  const docs = safeParse(record.document_urls);

  const run = (fn: () => Promise<any>, ok: string, fail: string) => async (reason?: string) => {
    try {
      await fn();
      toastSuccess(ok);
      refresh();
    } catch {
      toastError(fail);
    }
    void reason;
  };

  const approveCfg: ConfirmConfig = {
    title: 'Approve certificate',
    tone: 'indigo',
    confirmLabel: 'Approve',
    consequence: `Grants "${record.sport.name_en}" as an active specialty for ${record.trainer.name}.`,
    run: run(
      () =>
        dataProvider.update('specialty-certificates', {
          id: record.id,
          data: { approved: true },
          previousData: record,
        }),
      'Certificate approved',
      'Failed to approve',
    ),
  };
  const rejectCfg: ConfirmConfig = {
    title: 'Reject certificate',
    tone: 'destructive',
    confirmLabel: 'Reject',
    consequence: 'The trainer will see your reason and can resubmit.',
    reasonRequired: true,
    reasonChips: ['Illegible document', 'Does not match specialty', 'Certificate expired or invalid'],
    run: async (reason) =>
      run(
        () =>
          dataProvider.update('specialty-certificates', {
            id: record.id,
            data: { approved: false, rejection_reason: reason },
            previousData: record,
          }),
        'Certificate rejected',
        'Failed to reject',
      )(reason),
  };

  return (
    <div className="space-y-6 p-6">
      {/* Record header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar src={record.trainer.profile_picture_url} name={record.trainer.name} size={60} />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                {record.trainer.name}
              </h1>
              <CertStatusBadge status={record.status} />
            </div>
            <p className="mt-1 text-sm text-ink-2">
              {record.trainer.email}
              {record.trainer.username && ` · @${record.trainer.username}`}
            </p>
          </div>
        </div>

        {record.status === 'PENDING' && (
          <div className="flex flex-wrap items-center gap-2">
            <PillBtn variant="danger" onClick={() => setConfirm(rejectCfg)}>
              <XCircle className="size-4" />
              Reject
            </PillBtn>
            <PillBtn variant="primary" onClick={() => setConfirm(approveCfg)}>
              <CheckCircle className="size-4" />
              Approve
            </PillBtn>
          </div>
        )}
      </div>

      {/* Rejection reason banner */}
      {record.rejection_reason && (
        <div className="rounded-card border border-error/30 bg-error-bg px-4 py-3 text-sm text-error">
          <strong>Reason:</strong> {record.rejection_reason}
        </div>
      )}

      {/* Grace period banner */}
      {record.status === 'GRACE_PERIOD' && (
        <div className="rounded-card border border-warning/30 bg-warning-bg px-4 py-3 text-sm text-warning">
          <strong>Awaiting submission.</strong> This specialty was recently flagged as requiring
          certification. The trainer has until {fmtDate(record.grace_period_deadline)} to submit
          proof before it's automatically removed.
        </div>
      )}

      {/* Two-column body */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <DetailCard title="Specialty" icon={Award}>
            <SportLabel sport={record.sport} />
          </DetailCard>

          <DetailCard title="Submitted documents" icon={Award}>
            <DocumentGallery urls={docs} />
          </DetailCard>
        </div>

        <div className="space-y-6">
          <DetailCard title="Certificate">
            <div className="divide-y divide-hairline">
              <Fact label="Certificate ID" value={`#${record.id}`} />
              <Fact label="Trainer ID" value={record.trainer.id} />
              <Fact label="Status" value={<CertStatusBadge status={record.status} />} />
              <Fact label="Submitted" value={fmtDate(record.created_at)} />
              {record.status === 'GRACE_PERIOD' && (
                <Fact label="Grace deadline" value={fmtDate(record.grace_period_deadline)} />
              )}
            </div>
          </DetailCard>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title || ''}
        consequence={confirm?.consequence}
        tone={confirm?.tone || 'destructive'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        reasonRequired={confirm?.reasonRequired}
        reasonChips={confirm?.reasonChips}
        onConfirm={async (reason) => {
          if (confirm) await confirm.run(reason);
        }}
      />
    </div>
  );
};

export const SpecialtyCertificateShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <SpecialtyCertificateShowContent />
  </Show>
);
